import React, { useState, useEffect } from 'react';
import { PaymentMethod } from '../../../types';
import FormSection from '../FormSection';
import { StepNumberIcon } from '../../icons';
import type { PaymentFormProps } from './types/payment-form.types';
import { usePaymentForm } from './hooks/usePaymentForm';
import PaymentMethodSelector from './components/PaymentMethodSelector';
import CreditCardForm from './components/CreditCardForm';
import PixForm from './components/PixForm';
import BoletoForm from './components/BoletoForm';
import PaymentButtons from './components/PaymentButtons';
import ExpressCheckoutButton from './components/ExpressCheckoutButton';
import OrderBumpSection from '../../OrderBumpSection';

const PaymentForm: React.FC<PaymentFormProps> = (props) => {
  const { selectedMethod, creditCardData, errors, handleMethodChange, handleChange, handleSubmit } =
    usePaymentForm(props);
  const [hasWalletsAvailable, setHasWalletsAvailable] = useState(false);

  useEffect(() => {
    if (selectedMethod !== PaymentMethod.CREDIT_CARD) {
      setHasWalletsAvailable(false);
    }
  }, [selectedMethod]);

  const allowed = new Set((props.availablePaymentMethods || []).map((m) => String(m).toLowerCase()));
  const walletsEnabled =
    allowed.has(String(PaymentMethod.APPLE_PAY).toLowerCase()) || allowed.has(String(PaymentMethod.GOOGLE_PAY).toLowerCase());
  const walletsPublishableKey = props.wallets?.publishableKey;
  const showWallets = selectedMethod === PaymentMethod.CREDIT_CARD && walletsEnabled && walletsPublishableKey;

  const renderPaymentMethodForm = () => {
    switch (selectedMethod) {
      case PaymentMethod.CREDIT_CARD:
        return (
          <div className="space-y-4 pt-4 rounded-[10px] p-4 border border-gray-300 dark:border-gray-600">
            {showWallets && (
              <div className="space-y-2">
                <ExpressCheckoutButton
                  amount={
                    (() => {
                      const card = props.paymentMethodPrices?.CARD ?? 0;
                      const apple = props.paymentMethodPrices?.APPLE_PAY;
                      const google = props.paymentMethodPrices?.GOOGLE_PAY;
                      if (apple != null && google != null) return Math.min(apple, google);
                      if (apple != null) return apple;
                      if (google != null) return google;
                      return card;
                    })()
                  }
                  currency="brl"
                  publishableKey={walletsPublishableKey!}
                  onWalletsAvailable={setHasWalletsAvailable}
                />
              </div>
            )}
            {hasWalletsAvailable && (
              <p className="text-sm font-medium text-text-secondary dark:text-gray-400 text-center border-b border-gray-300 dark:border-gray-600 pb-2">Ou</p>
            )}
            <CreditCardForm
              creditCardData={creditCardData}
              errors={errors}
              onChange={(e) => handleChange(e, PaymentMethod.CREDIT_CARD)}
              installmentOptions={props.installmentOptions}
              on3DSProcessing={props.on3DSProcessing}
              on3DSError={props.on3DSError}
              onSubmit={props.onCreditCardSubmit}
              creditCardPrimaryGateway={props.creditCardPrimaryGateway}
              stripePublishableKey={props.stripePublishableKey}
              pagouPublicKey={props.pagouPublicKey}
              pagouProduction={props.pagouProduction}
              pagarmePublicKey={props.pagarmePublicKey}
              shouldLoad3DS={props.creditCardGatewayRequires3DS}
              onCardElementReady={props.onCardElementReady}
              onReusableToken={props.onReusableToken}
              hasOneClickUpsell={props.hasOneClickUpsell}
            />
          </div>
        );
      case PaymentMethod.PIX:
        return <PixForm price={props.paymentMethodPrices?.PIX} />;
      case PaymentMethod.BOLETO:
        return <BoletoForm price={props.paymentMethodPrices?.BOLETO} />;
      default:
        return null;
    }
  };

  return (
    <div className="container mx-auto max-w-2xl">
      <FormSection title="Informações de Pagamento" icon={<StepNumberIcon displayNumber={3} />}>
        <form onSubmit={handleSubmit} className="space-y-6" data-payment-form>
          {/* Campo hidden com método de pagamento selecionado */}
          <input type="hidden" name="paymentMethod" value={selectedMethod} />

          <PaymentMethodSelector
            selectedMethod={selectedMethod}
            onMethodChange={handleMethodChange}
            error={errors.paymentMethod}
            availablePaymentMethods={props.availablePaymentMethods}
            paymentMethodDiscounts={props.paymentMethodDiscounts}
            paymentMethodPrices={props.paymentMethodPrices}
          />

          {renderPaymentMethodForm()}

          {props.orderBumps && props.orderBumps.length > 0 && props.onOrderBumpsChange && (
            <OrderBumpSection
              bumps={props.orderBumps}
              acceptedNanoIds={props.acceptedOrderBumpNanoIds ?? []}
              onToggle={props.onOrderBumpsChange}
            />
          )}

          <PaymentButtons />
        </form>
      </FormSection>
    </div>
  );
};

export default PaymentForm;
